import { IMTransactionProfileLookupLayout, CodeLookupLayout } from "../../common/lookups/Layouts/ImLayouts";
import { ACDepartmentLayout } from "../../common/lookups/Layouts/ACLayouts";
import { FAFundLayout } from "../../common/lookups/Layouts/FALayouts";
import { CAJobLayout } from "../../common/lookups/Layouts/CALayouts";
import { IDParticipantLookupLayout, IDFuelAndMiscTaxLookupLayout } from "../../common/lookups/Layouts/IdLayouts";

// Tab schemas for the IM Transaction Entry form (Delphi uIMD0019). Consumed by the tab components via
// the dynamic FormField builder. Coded fields render as descriptive dropdowns (Architecture rule 3);
// options are injected in PrimaryTab.onFormLoad.
const f = (name, label, type, visible, extra) => ({
    name, tableName: "IMTRANSACTIONHEADER", label, type,
    required: false, visible, isLabelChanged: false, ...(extra || {}),
});

// User-defined passthrough columns (Code/Reference/Date/Quantity 1..10) — shown on the User Defined tab.
// Delphi edtUSERDEFINEDCODE_1..10 are lookup edits over IMCODES, not free-text boxes: each one is
// scoped to its own CODETYPE, taken from the control's Tag and formatted two digits
// (uIMD0019.PAS:2009 - BaseWhere := 'CodeType = ' + FormatFloat('00', Tag)). The Tags decode to
// 89..98 for header codes 1..10 in order, so the code type is 88 + n.
const codes = () => Array.from({ length: 10 }, (_, i) => f(`CODE${i + 1}`, `Code ${i + 1}`, "lookup", true, {
    layout: CodeLookupLayout({ codeType: String(89 + i) }),
}));
const references = () => Array.from({ length: 10 }, (_, i) => f(`REFERENCE${i + 1}`, `Reference ${i + 1}`, "text", true));
const dates = () => Array.from({ length: 10 }, (_, i) => f(`DATE${i + 1}`, `Date ${i + 1}`, "date", true));
const quantities = () => Array.from({ length: 10 }, (_, i) => f(`QUANTITY${i + 1}`, `Quantity ${i + 1}`, "number", true));

export const PrimaryLayout = [
    // Left column — the core header fields (Delphi left group).
    {
        groupId: 1,
        label: "",
        fields: [
            f("RECORD", "Record #", "text", true, { localizedField: "KEYSEQUENCE", readOnly: true }),
            // Delphi edtP_PROFILE is a lookup edit with a popup search over IMTRANSACTIONDP, not a
            // dropdown of every profile: it searches on PROFILECODE and loads on demand.
            f("RPROFILE", "Profile", "lookup", true, {
                required: true,
                layout: IMTransactionProfileLookupLayout(),
            }),
            f("TRANSACTIONTYPE", "Trans Type", "select", true, { searchEnabled: false }),
            f("REFERENCE", "Reference #", "text", true),
            f("TRANSACTIONDATE", "Transaction", "date", true),
            f("DESCRIPTION", "Description", "text", true),
            f("STAGE", "Stage", "select", true, { searchEnabled: false }),
        ],
    },
    // Status + fiscal (Delphi middle column, above the box). Comment / Import File live on the Additional tab.
    {
        groupId: 2,
        label: "",
        fields: [
            f("STATUS", "Status", "select", true, { searchEnabled: false }),
            f("FISCALYEAR", "Fiscal Year", "number", true, { readOnly: true }),
            f("FISCALPERIOD", "Fiscal Period", "number", true, { readOnly: true }),
        ],
    },
    // Default Management Units (Delphi middle-column box) — optional GL dimension defaults.
    {
        groupId: 3,
        label: "Default Management Units",
        fields: [
            // Delphi edtR_DEFAULTDEPARTMENT / _FUND / _JOB are lookup edits with popup search over
            // ACDEPARTMENT / FAFUND / CAJOB, not dropdowns of every row. Reusing the shared layouts
            // these tables already have rather than re-declaring the columns here.
            f("RDEFAULTDEPARTMENT", "Department", "lookup", true, { layout: ACDepartmentLayout }),
            f("RDEFAULTFUND", "Fund", "lookup", true, { layout: FAFundLayout }),
            f("RDEFAULTJOB", "Job", "lookup", true, { layout: CAJobLayout }),
        ],
    },
    // Totals (Delphi right column) — read-only rollups from the detail lines.
    {
        groupId: 4,
        label: "Totals",
        fields: [
            f("TOTALLINES", "Total Lines", "text", true, { readOnly: true }),
            f("TOTALQUANTITY", "Total Quantity", "text", true, { readOnly: true }),
            f("TOTALAMOUNT", "Total Amount", "text", true, { readOnly: true }),
        ],
    },
];

// Additional tab (Delphi uIMD0019 "Additional") — Comment + the read-only imported file name.
export const AdditionalLayout = [
    {
        groupId: 1,
        label: "",
        fields: [
            f("COMMENT", "Comment", "textarea", true),
            f("IMPORTFILENAME", "Import File Name", "text", true),
        ],
    },
];

// User Defined tab (Delphi uIMD0019 "User Defined") — the 4x10 user-defined passthroughs grouped by kind
// (Codes / References / Dates / Quantities), plus the user-defined comment.
// Delphi pagBottomUserDefined splits this tab in two (uIMD0019.dfm): tabBottomUserDefinedCodes
// "&Codes" carries the Code, Date and Quantity series, and tabBottomUserDefinedReferences
// "&References" carries the Reference series plus USERDEFINEDCOMMENT. One flat schema put Dates and
// Quantities on the wrong side of that split and lost the nesting entirely.
export const UserDefinedCodesLayout = [
    { groupId: 1, label: "Codes", fields: codes() },
    { groupId: 2, label: "Dates", fields: dates() },
    { groupId: 3, label: "Quantities", fields: quantities() },
];

export const UserDefinedReferencesLayout = [
    { groupId: 1, label: "References", fields: references() },
    { groupId: 2, label: "", fields: [f("USERDEFINEDCOMMENT", "Comment", "textarea", true)] },
];

// Details tab -> "Primary" sub-tab schema (Delphi uIMD0019 Details/Primary). Bound to the SELECTED detail
// line (IMTransactionDetailDto, camelCase keys), read-only display for now. Field names match the grid
// row object keys so the shared selected-row object populates the form directly.
const df = (name, label, type, extra) => ({
    name, tableName: "IMTRANSACTIONDETAIL", label, type,
    required: false, visible: true, isLabelChanged: false, readOnly: true, ...(extra || {}),
});

export const DetailPrimaryLayout = [
    {
        groupId: 1,
        label: "Item",
        fields: [
            df("itemDescription", "Item", "text"),
            df("itemUom", "UOM", "text"),
            df("itemNetAvailable", "Net Available", "text"),
        ],
    },
    {
        groupId: 2,
        label: "Lot",
        fields: [
            df("lotDescription", "Lot", "text"),
            df("lotUom", "UOM", "text"),
            df("lotNetAvailable", "Net Available", "text"),
        ],
    },
];

// Editable detail field (not read-only), bound to the selected line by camelCase key.
const ef = (name, label, type, extra) => ({
    name, tableName: "IMTRANSACTIONDETAIL", label, type,
    required: false, visible: true, isLabelChanged: false, ...(extra || {}),
});

// Details -> "User Defined" sub-tab (Delphi uIMD0019 Details/User Defined) — the line's user-defined
// Code/Reference/Date/Quantity 1..5 passthroughs (detail carries 5 of each, vs 10 on the header).
const range5 = [1, 2, 3, 4, 5];
export const DetailUserDefinedLayout = [
    { groupId: 1, label: "Codes", fields: range5.map((i) => ef(`code${i}`, `Code ${i}`, "text")) },
    { groupId: 2, label: "References", fields: range5.map((i) => ef(`reference${i}`, `Reference ${i}`, "text")) },
    { groupId: 3, label: "Dates", fields: range5.map((i) => ef(`date${i}`, `Date ${i}`, "date")) },
    { groupId: 4, label: "Quantities", fields: range5.map((i) => ef(`quantity${i}`, `Quantity ${i}`, "number")) },
];

// Details -> "Miscellaneous" sub-tab (Delphi uIMD0019 "EF Interface") — meter / participant / fuel fields.
export const DetailMiscLayout = [
    {
        groupId: 1,
        label: "EF Interface",
        fields: [
            ef("meterId", "Meter ID", "select"),
            ef("participantId", "Participant", "lookup", { layout: IDParticipantLookupLayout }),
            ef("meterReading", "Meter Reading", "number"),
        ],
    },
    {
        groupId: 2,
        label: "",
        fields: [
            ef("fuelReference", "Fuel Ref.", "text"),
            ef("fuelCity", "City", "text"),
            ef("fuelTaxId", "State", "lookup", { layout: IDFuelAndMiscTaxLookupLayout }),
        ],
    },
];

// Details -> "Comments" sub-tab (Delphi uIMD0019 Details/Comments) — the line's comment + user comment.
export const DetailCommentsLayout = [
    { groupId: 1, label: "", fields: [ef("comment", "Comment", "textarea")] },
    { groupId: 2, label: "", fields: [ef("userDefinedComment", "User Comment", "textarea")] },
];

// Details -> "Accounting" sub-tab (Delphi uIMD0019 Details/Accounting) — GL dimensions + vendor/asset/date
// for the selected line. Coded fields are server-loaded dropdowns (options injected in AccountingTab).
export const DetailAccountingLayout = [
    {
        groupId: 1,
        label: "",
        fields: [
            ef("accountId", "Account", "select"),
            ef("glDepartmentId", "Department", "select"),
            ef("fundId", "Fund", "select"),
            ef("jobId", "Job", "select"),
            ef("activityId", "Activity", "select"),
        ],
    },
    {
        groupId: 2,
        label: "",
        fields: [
            ef("transactionDate", "Date", "date"),
            ef("equipmentId", "Asset ID", "select"),
            ef("vendorId", "Vendor", "select"),
        ],
    },
];
